import { type QueryFunctionContext } from "@tanstack/react-query";
import { completes, lineChartFactory, starts } from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { type ChartPeriod } from "hooks/useChartPeriodMeasure";
import { embeddedVideosDataService } from "../../services/embeddedVideosDataService";
import { type BasePerformanceRequestFilterParams } from "features/Library/Common/models";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";

type QueryFilterEmbeddedVideoId = QueryFunctionContext<
  [_: string, filter: BasePerformanceRequestFilterParams, embeddedVideoId: number],
  unknown
>;
type QueryFilterEmbeddedVideoIdPeriod = QueryFunctionContext<
  [_: string, filter: BasePerformanceRequestFilterParams, embeddedVideoId: number, period: ChartPeriod],
  unknown
>;

export const getLineChartQuery = async ({ queryKey, signal }: QueryFilterEmbeddedVideoIdPeriod) => {
  const { data } = await embeddedVideosDataService.getEmbeddedVideoLineChart(
    queryKey[1],
    queryKey[2],
    queryKey[3],
    signal!,
  );

  return lineChartFactory(data);
};

export const getEngagementQuery = async ({ queryKey, signal }: QueryFilterEmbeddedVideoId) => {
  const { data } = await embeddedVideosDataService.getEmbeddedVideoEngagement(queryKey[1], queryKey[2], signal!);

  const usageBarData: DataPoint[] = [
    {
      id: data.StartedCount.toString().toLowerCase(),
      category: starts,
      value: data.StartedCount,
      fillColor: reportingColors.blue,
    },
    {
      id: data.CompletedCount.toString().toLowerCase(),
      category: completes,
      value: data.CompletedCount,
      fillColor: reportingColors.yellow,
    },
  ];

  const cards: CardReportingItem[] = [
    {
      statDescription: starts,
      popupBody: "The sum of how often this video has been started.",
      stat: data.StartedCount,
    },
    {
      statDescription: completes,
      popupBody: "The sum of how often this video has been completed.",
      stat: data.CompletedCount,
    },
    {
      statDescription: "Completion Rate",
      popupBody: "The percentage of videos completed compared to the sum of how often this video has been started.",
      stat: data.CompletionRate * 100,
      isStatPercentage: true,
    },
  ];

  return { cards, usageBarData };
};

export const getPeopleDetailsQuery = async ({ queryKey, signal }: QueryFilterEmbeddedVideoId) =>
  (await embeddedVideosDataService.getEmbeddedVideoPeopleDetails(queryKey[1], queryKey[2], signal!)).data;
